import {useContext, useEffect, useRef} from 'react';
import {MessageDisplayerUtility} from "../MessageDisplayerUtility";
import {LeaveWithoutSavingWarningUtility} from "../LeaveWithoutSavingWarningUtility";
import {GlobalContext} from "../../admin-frame/AdminFrame";
import {useRenderableDataStore} from "./useRenderableDataStore";
import {GenericModel} from "./GenericModel";

// Engine for a wrapper component for a form, where wrapper is in charge of updating models defined by the form. Updating includes "deleting" which is really just deactivating.
export function useModelUpdateEngine(
    modelFetchApiFunction: Function,
    modelUpdateApiFunction: Function,
    modelId: number | string,
    modelName: string,
    updateCallback: Function,
    messageAdditionalConfig: any
) {
    const context = useContext(GlobalContext);
    const updateEngineDataStore = useRenderableDataStore({
        loading: true,
        model: undefined
    });

    // tracks whether the component is still mounted, so fetch responses that
    // arrive after unmount can be discarded rather than written into the store
    const isMountedRef = useRef(true);

    // on mount, fetch model from API and put it in state
    useEffect(function () {
        isMountedRef.current = true;
        refreshModel()
        return function () {
            isMountedRef.current = false;
        };
    }, [modelId]);

    function refreshModel() {
        updateEngineDataStore.updateAndRender({loading: true})
        modelFetchApiFunction(modelId)
            .then(function (model: GenericModel) {
                if (!isMountedRef.current) {
                    return;
                }
                updateEngineDataStore.updateAndRender({
                    model: model,
                    loading: false,
                });
            })
            .catch(function (error: object) {
                updateEngineDataStore.updateAndRender({loading: false});
                console.log(`Error fetching ${modelName}: `, error);
                MessageDisplayerUtility.error(`Error fetching ${modelName}.`, messageAdditionalConfig);
            });
    }

    function updateModel(formModelData: object, disableNavigationBlocker: Function) {
        updateEngineDataStore.updateAndRender({loading: true})
        modelUpdateApiFunction(formModelData)
            .then(() => {
                disableNavigationBlocker()
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings();
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully updated ${modelName}.`, messageAdditionalConfig);
                    if (updateCallback) {
                        updateCallback();
                    }
                }, 0)
            })
            .catch((error: object) => {
                updateEngineDataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while updating the ${modelName}.`, messageAdditionalConfig);
            });
    }

    return {
        dataStore: updateEngineDataStore,
        refreshModel: refreshModel,
        updateModel: updateModel,
    };
}
